import React, { useContext, ReactElement } from 'react';

import { StyleSheet, css, theme } from '../Common/styling';
import { resetPassword } from './usersApi';
import { useNotify } from '../Common/snackbarHooks';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../Common/AuthContext';
import { AuthContextValue } from '../Common/types';
import { LockOutlined as LockOutlinedIcon } from '@mui/icons-material';
import { Avatar, Button, FormControl, Input, InputLabel, Paper, Typography } from '@mui/material';
import { HandleError } from '../Common/ErrorHandling/ErrorHelper';
import { validationSchema } from './resetPasswordSchema';
import { Formik, ErrorMessage } from 'formik';

const styles = StyleSheet.create({
  loginView: {
    backgroundColor: theme.themeGreenGreyLight,
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  loginCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    maxWidth: '340px',
  },

  avatar: {
    margin: '10px',
  },

  loginButton: {
    marginTop: '40px',
  },
});

const ResetPasswordPage: React.FC = () => {
  // hooks
  const navigate = useNavigate();
  const { notifySuccess, notifyError } = useNotify();
  const { logout, isAuth } = useContext(AuthContext) as AuthContextValue;

  const getUrlParameter = (key: string): string => {
    key = key.replace(/[[]/, '\\[').replace(/[\]]/, '\\]');
    const regex = new RegExp('[\\?&]' + key + '=([^&#]*)');
    const results = regex.exec(window.location.search);
    return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
  };

  const handleSubmit = (values: any): void => {
    if (values.password === values.confirm_password) {
      resetPassword({
        token: getUrlParameter('token'),
        password: values.password,
      })
        .then(() => {
          // Log the previous user out, if they have authenticated so that
          // the new user does not accidentally login to a wrong account after password reset.
          if (isAuth) {
            logout();
          }
          notifySuccess('Lösenordet är ändrat');
          navigate('/login');
        })
        .catch((error) => {
          notifyError(`Det gick inte att uppdatera lösenordet. \n${HandleError(error.response.data)}`);
        });
    } else {
      notifyError('Lösenorden matchar inte');
    }
  };

  return (
    <div className={css(styles.loginView)}>
      <Paper className={css(styles.loginCard)}>
        <Avatar className={css(styles.avatar)}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography variant="h5">Sätt lösenordet</Typography>
        <div>
          <Formik
            initialValues={{ password: '', confirm_password: '' }}
            validationSchema={validationSchema}
            validateOnMount={true}
            validateOnChange={true}
            onSubmit={(values, actions): void => {
              handleSubmit(values);
            }}
          >
            {({ values, handleChange, handleSubmit }): ReactElement => (
              <form onSubmit={handleSubmit}>
                <FormControl variant="standard" margin="normal" required fullWidth>
                  <InputLabel htmlFor="email">Nya lösenordet</InputLabel>
                  <Input
                    id="password"
                    name="password"
                    value={values.password}
                    onChange={handleChange}
                    type="password"
                  />
                  <ErrorMessage
                    name="password"
                    render={(msg) => (
                      <div>
                        <span className="text-red-600 text-xs">{msg}</span>
                      </div>
                    )}
                  />
                </FormControl>
                <FormControl variant="standard" margin="normal" required fullWidth>
                  <InputLabel htmlFor="password">Bekräfta lösenordet</InputLabel>
                  <Input
                    name="confirm_password"
                    type="password"
                    id="confirm_password"
                    value={values.confirm_password}
                    onChange={handleChange}
                  />
                  <ErrorMessage
                    name="confirm_password"
                    render={(msg) => (
                      <div>
                        <span className="text-red-600 text-xs">{msg}</span>
                      </div>
                    )}
                  />
                </FormControl>
                <Button type="submit" fullWidth variant="contained" color="primary" className={css(styles.loginButton)}>
                  Ändra lösenordet
                </Button>
              </form>
            )}
          </Formik>
        </div>
      </Paper>
    </div>
  );
};

export default ResetPasswordPage;
